import { FinishedUnaryCall, MethodInfo } from '@protobuf-ts/runtime-rpc'
import { Status } from 'shared/gen/pkg/proto/response'
import {
  SystemError,
  UserError,
  isK2Error,
  isSystemError,
  isUserError,
} from 'shared/utils/errors'
import {
  handleRpcResponse,
  isUserFacingGrpcStatusCode,
} from 'shared/utils/errors/rpc.utils'
import { GuaranteedResponse, RequiredK2Response } from './k2-use-case'

// WARNING: This file cannot import server-only code because it's being used by client code, please keep it that way

/**
 * This method
 * - ensures the k2 response has a status.code of "OK" and a response.info.status of "SUCCESS"
 * - It then removes symbols from the response that can't be serialized by nextjs
 * - It then finally returns the "response" portion of the k2 response to match the expected shape of each request
 */
export function processK2Response<T extends RequiredK2Response<object>>(
  baseResponse: T,
): GuaranteedResponse<T['response']> {
  const results = handleRpcResponse(baseResponse)

  return removeSymbols({
    ...results.response,
    info: {
      status: Status.SUCCESS,
      message: results.response?.info?.message ?? 'success',
    },
  })
}

/**
 * # Processing
 * - This method ensures that a user-friendly error message is returned in a UserError or a handled K2GenericError
 * - If a K2GenericError is unhandled, the default error message is used
 * - If the error is already a UserError, it is returned as is
 * - If the error is an instance of Error, it is wrapped in a UserError
 * - If the error is anything else, a new UserError is created with the default message
 *
 * # Logging
 * - It also sets a return error's 'handled' property to true, which will be ignored by the logger if logged as an error
 * - For this reason, we need to log errors from K2's grpc-interface before returning them from this processor
 *
 * # Usage
 * ```
 * try {
 *  ...
 * } catch (error) {
 *  logger.error(error, {feature: 'my-feature'})
 *  throw processK2Error(error, 'default error message')
 * }
 * ```
 *
 * @deprecated - use `processServerActionError` instead
 */
export function processK2Error(
  error: unknown,
  defaultMessage: string,
): UserError | SystemError {
  if (isK2Error(error)) {
    return error.toApplicationError(defaultMessage)
  } else if (isUserError(error)) {
    return error
  } else if (isSystemError(error)) {
    const exposeUserFacingGrpcMessage =
      isUserFacingGrpcStatusCode(error.metadata?.grpcStatusCode) &&
      Boolean(error.message) &&
      error.message !== 'Unknown Error'
    error.userMessage =
      error.handled || exposeUserFacingGrpcMessage
        ? error.message
        : defaultMessage
    return error
  } else if (error instanceof Error) {
    return new UserError(defaultMessage, { originalError: error })
  } else {
    return new UserError(defaultMessage)
  }
}

/**
 * @protobuf-ts adds symbols to response objects, which aren't supported in
 * React components. This is a simple utility that will remove symbols from
 * response objects, allowing you to pass them directly to React components
 */
export function removeSymbols<
  T extends FinishedUnaryCall<object, object>['response'],
>(data: T): T {
  return JSON.parse(JSON.stringify(data))
}

export const shouldReroute = (
  method: MethodInfo,
  reroute: string | undefined,
): boolean => {
  if (!reroute) {
    return false
  }

  const rerouteList = reroute.split(',')
  // check for service-level match or specific service/method match
  return rerouteList.some(
    (entry) =>
      entry === method.service.typeName ||
      entry === `${method.service.typeName}/${method.name}`,
  )
}
